'use client'

import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { motion, AnimatePresence } from 'framer-motion'
import ErrorBoundary from '@/components/ui/ErrorBoundary'
import { usePathname } from 'next/navigation'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuIndicator,
  NavigationMenuViewport,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu'
import { cn } from '@/utilities/ui'
import { getLinkHref as getLinkHrefUtil, type PayloadLinkData } from '@/components/Link'
import { Media } from '@/components/Media'
import type { Header as HeaderType, Logo as LogoType } from '@/payload-types'
import type { LatestProject } from '@/utilities/getLatestProjects'
import { ThemeToggle } from '@/components/ThemeToggle'

interface HeaderClientProps {
  data: HeaderType
  latestProjects: LatestProject[]
  logo?: LogoType | null
}

// Type helper to extract link type from Header
type HeaderNavLink = NonNullable<HeaderType['navItems']>[number]['link']
type HeaderProductGroupLink = NonNullable<HeaderType['productGroups']>[number]['link']
type HeaderLink = HeaderNavLink | HeaderProductGroupLink

function HeaderContent({ data, latestProjects, logo }: HeaderClientProps) {
  const pathname = usePathname()
  const [activeItem, setActiveItem] = useState('Produkte')
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)
  const lastScrollYRef = useRef(0) // Ref for scroll handler to avoid recreating on every scroll
  const [headerHeight, setHeaderHeight] = useState<number | null>(null)
  const [openDesktopDropdown, setOpenDesktopDropdown] = useState<string | null>(null)
  const headerRef = useRef<HTMLElement | null>(null)
  const hasUserScrolledRef = useRef<boolean>(false)
  const initialScrollYRef = useRef<number | null>(null)

  const navItems = data?.navItems || []
  const productGroups = data?.productGroups || []

  // Helper function to get href from link - converts Header link to PayloadLinkData format
  const getLinkHref = (link: HeaderLink | null | undefined): string | null => {
    if (!link) return null
    // Convert Header link format to PayloadLinkData format
    const payloadLink: PayloadLinkData = {
      type: link.type || null,
      reference: link.reference || null,
      url: link.url || null,
      phone: link.phone || null,
      email: link.email || null,
      label: link.label || null,
      anchor: link.anchor || null,
      openInNewTab: link.openInNewTab || null,
    }
    return getLinkHrefUtil(payloadLink)
  }

  // Check if a nav item is "Produkte"
  const isProdukteItem = (link: NonNullable<HeaderType['navItems']>[number]['link']): boolean => {
    const href = getLinkHref(link)
    return link.label?.toLowerCase() === 'produkte' || href === '/produkte'
  }

  // Check if a nav item is "Projekte"
  const isProjekteItem = (link: NonNullable<HeaderType['navItems']>[number]['link']): boolean => {
    const href = getLinkHref(link)
    return link.label?.toLowerCase() === 'projekte' || href === '/projekte'
  }

  // Reset scroll tracking on route change
  useEffect(() => {
    // Reset state when pathname changes - header should be visible on new page
    hasUserScrolledRef.current = false
    initialScrollYRef.current = null
    lastScrollYRef.current = 0
    setIsVisible(true)
    setLastScrollY(0)

    // Set initial scroll position after a brief delay to account for anchor navigation
    if (typeof window !== 'undefined') {
      const setInitialScroll = () => {
        initialScrollYRef.current = window.scrollY
        lastScrollYRef.current = window.scrollY
        setLastScrollY(window.scrollY)
      }
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        setTimeout(setInitialScroll, 100)
      })
    }
  }, [pathname])

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      // Initialize initial scroll position if not set
      if (initialScrollYRef.current === null) {
        initialScrollYRef.current = currentScrollY
        lastScrollYRef.current = currentScrollY
        setLastScrollY(currentScrollY)
        return
      }

      // Mark that user has started scrolling (scroll position changed from initial)
      if (!hasUserScrolledRef.current && currentScrollY !== initialScrollYRef.current) {
        hasUserScrolledRef.current = true
      }

      // Only apply hide/show logic after user has started scrolling
      if (!hasUserScrolledRef.current) {
        // User hasn't scrolled yet, keep header visible
        lastScrollYRef.current = currentScrollY
        return
      }

      // Normal scroll behavior: show navbar when scrolling up or at the top
      // Using ref instead of state to avoid recreating handler on every scroll
      if (currentScrollY < lastScrollYRef.current || currentScrollY < 100) {
        setIsVisible(true)
      }
      // Hide navbar when scrolling down and not at the top
      else if (currentScrollY > lastScrollYRef.current && currentScrollY > 100) {
        setIsVisible(false)
      }

      lastScrollYRef.current = currentScrollY
      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, []) // Empty dependency array - handler is stable now

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  useEffect(() => {
    if (headerHeight != null && typeof window !== 'undefined') {
      try {
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`)
      } catch {}
    }
  }, [headerHeight])

  // Dynamically update visible header offset so sticky subnav can sit below it
  useEffect(() => {
    const el = headerRef.current
    if (!el) return

    let raf = 0
    const updateOffset = () => {
      const rect = el.getBoundingClientRect()
      const height = rect.height
      const visible = Math.min(height, Math.max(0, rect.bottom))
      try {
        document.documentElement.style.setProperty('--header-offset', `${visible}px`)
      } catch {}
    }

    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        updateOffset()
        raf = 0
      })
    }

    updateOffset()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])

  return (
    <motion.header
      className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-[var(--dark)]/90 via-[var(--dark)]/40 to-transparent group hover:bg-[var(--dark)]/95 transition-colors duration-300"
      animate={{ y: isVisible ? 0 : -100 }}
      transition={{
        duration: 0.5,
        ease: 'easeInOut',
      }}
      ref={(el) => {
        if (!el) return
        headerRef.current = el as unknown as HTMLElement
        const measure = () => setHeaderHeight(el.getBoundingClientRect().height)
        measure()
        // ResizeObserver for dynamic content changes
        try {
          const ro = new ResizeObserver(() => measure())
          ro.observe(el)
        } catch {
          // Fallback to window resize
          window.addEventListener('resize', measure)
        }
      }}
    >
      {/* Hotline Info Banner */}
      <div className="container mx-auto px-4 md:px-8 py-3 font-metropolis">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link href="/" className="block">
              {logo?.headerLogo?.dark && typeof logo.headerLogo.dark === 'object' ? (
                <Media
                  resource={logo.headerLogo.dark}
                  alt={logo.headerLogo.dark.alt || 'Lüchinger Metallbau Logo'}
                  imgClassName="h-[35px] md:h-[50px] w-auto"
                  priority
                  disableBlur={true}
                />
              ) : (
                <Image
                  src="/images/logo/LM-Logo-Links-neg.png"
                  alt="Lüchinger Metallbau Logo"
                  width={239}
                  height={50}
                  className="h-[35px] md:h-[50px] w-auto"
                  priority
                />
              )}
            </Link>
          </div>

          {/* Desktop Navigation with hover sub-menus */}
          <div className="hidden xl:flex items-center">
            <NavigationMenu>
              <NavigationMenuList aria-label="Hauptnavigation">
                {navItems.map((navItem, i) => {
                  const { link } = navItem
                  const href = getLinkHref(link)
                  const navItemKey = navItem.id ?? `${link.label ?? 'nav-item'}-${i}`
                  const childLinks =
                    navItem.children?.reduce<{ href: string; label: string; id?: string | null }[]>(
                      (acc, child) => {
                        const childHref = getLinkHref(child.link)
                        const childLabel = child.link?.label
                        if (!childHref || !childLabel) {
                          return acc
                        }
                        acc.push({
                          href: childHref,
                          label: childLabel,
                          id: child.id,
                        })
                        return acc
                      },
                      [],
                    ) ?? []
                  const hasChildren = childLinks.length > 0

                  // Produkte with submenu
                  if (isProdukteItem(link)) {
                    return (
                      <NavigationMenuItem key={i}>
                        {href ? (
                          <NavigationMenuTrigger
                            asChild
                            className={cn(
                              pathname.startsWith('/produkte')
                                ? 'border-b border-text-white'
                                : 'border-b border-transparent hover:border-text-white/60',
                            )}
                          >
                            <Link href={href}>{link.label || 'Produkte'}</Link>
                          </NavigationMenuTrigger>
                        ) : (
                          <NavigationMenuTrigger
                            className={cn(
                              pathname.startsWith('/produkte')
                                ? 'border-b border-text-white'
                                : 'border-b border-transparent hover:border-text-white/60',
                            )}
                          >
                            {link.label || 'Produkte'}
                          </NavigationMenuTrigger>
                        )}
                        <NavigationMenuContent className="w-[min(100vw,900px)] xl:w-[900px]">
                          <div className="px-5 py-7 xl:px-8 w-full">
                            {href ? (
                              <div className="mb-5">
                                <Link
                                  href={href}
                                  className="inline-flex font-ui text-sm uppercase tracking-wide text-text-primary hover:text-text-brand transition-colors"
                                >
                                  Alle Produkte anzeigen
                                </Link>
                              </div>
                            ) : null}
                            <ul className="grid grid-cols-2 xl:grid-cols-4 gap-5">
                              {productGroups.map((productGroup, index) => {
                                const href = productGroup.link
                                  ? getLinkHref(productGroup.link)
                                  : null
                                const image = productGroup.image
                                const hasImage =
                                  image && typeof image === 'object' && 'url' in image

                                if (!href || !hasImage) return null

                                return (
                                  <li key={`${productGroup.title}-${index}`} className="">
                                    <NavigationMenuLink asChild>
                                      <Link
                                        href={href}
                                        className="group/item block transition-colors focus:outline-hidden hover:bg-[var(--background-secondary)]/10"
                                      >
                                        <div className="relative aspect-[4/3] overflow-hidden bg-black/20">
                                          <Media
                                            resource={image}
                                            alt={productGroup.title || 'Product'}
                                            fill
                                            imgClassName="object-cover transition-transform duration-300 group-hover:scale-105"
                                            size="(min-width: 1280px) 220px, 50vw"
                                          />
                                        </div>
                                        <div className="mt-4">
                                          <div className="text-text-primary text-sm font-medium uppercase tracking-wide transition-colors group-hover/item:text-text-brand">
                                            {productGroup.title}
                                          </div>
                                          {productGroup.description ? (
                                            <div className="text-text-secondary text-sm">
                                              {productGroup.description}
                                            </div>
                                          ) : null}
                                        </div>
                                      </Link>
                                    </NavigationMenuLink>
                                  </li>
                                )
                              })}
                            </ul>
                          </div>
                        </NavigationMenuContent>
                      </NavigationMenuItem>
                    )
                  }

                  // Projekte with submenu (latest projects from PayloadCMS)
                  if (isProjekteItem(link) && latestProjects.length > 0) {
                    return (
                      <NavigationMenuItem key={i}>
                        {href ? (
                          <NavigationMenuTrigger
                            asChild
                            className={cn(
                              pathname.startsWith('/projekte')
                                ? 'border-b border-text-white'
                                : 'border-b border-transparent hover:border-text-white/60',
                            )}
                          >
                            <Link href={href}>{link.label || 'Projekte'}</Link>
                          </NavigationMenuTrigger>
                        ) : (
                          <NavigationMenuTrigger
                            className={cn(
                              pathname.startsWith('/projekte')
                                ? 'border-b border-text-white'
                                : 'border-b border-transparent hover:border-text-white/60',
                            )}
                          >
                            {link.label || 'Projekte'}
                          </NavigationMenuTrigger>
                        )}
                        <NavigationMenuContent className="w-[min(100vw,720px)] xl:w-[720px]">
                          <div className="px-5 py-7 xl:px-8 w-full">
                            {href ? (
                              <div className="mb-5">
                                <Link
                                  href={href}
                                  className="inline-flex font-ui text-sm uppercase tracking-wide text-text-primary hover:text-text-brand transition-colors"
                                >
                                  Alle Projekte anzeigen
                                </Link>
                              </div>
                            ) : null}
                            <ul className="grid grid-cols-2 xl:grid-cols-3 gap-5">
                              {latestProjects.slice(0, 3).map((project) => (
                                <li key={project.id} className="">
                                  <NavigationMenuLink asChild>
                                    <Link
                                      href={`/projekte/${project.slug}`}
                                      className="group/item block transition-colors focus:outline-hidden hover:bg-[var(--background-secondary)]/10"
                                    >
                                      <div className="relative aspect-[4/3] overflow-hidden bg-black/20">
                                        {project.featuredImage ? (
                                          <Media
                                            resource={project.featuredImage}
                                            alt={project.featuredImage.alt || project.title}
                                            fill
                                            imgClassName="object-cover transition-transform duration-300 group-hover:scale-105"
                                            size="(min-width: 1280px) 220px, 50vw"
                                          />
                                        ) : (
                                          <div className="flex items-center justify-center h-full text-text-white/40">
                                            No Image
                                          </div>
                                        )}
                                      </div>
                                      <div className="mt-4">
                                        <div className="text-text-primary text-sm font-medium uppercase tracking-wide transition-colors group-hover/item:text-text-brand">
                                          {project.title}
                                        </div>
                                      </div>
                                    </Link>
                                  </NavigationMenuLink>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </NavigationMenuContent>
                      </NavigationMenuItem>
                    )
                  }

                  if (hasChildren) {
                    return (
                      <NavigationMenuItem
                        key={i}
                        className="relative"
                        onMouseEnter={() => setOpenDesktopDropdown(navItemKey)}
                        onMouseLeave={() =>
                          setOpenDesktopDropdown((current) =>
                            current === navItemKey ? null : current,
                          )
                        }
                      >
                        <NavigationMenuLink
                          asChild
                          className={cn(
                            pathname === href || (href && href !== '/' && pathname.startsWith(href))
                              ? `${navigationMenuTriggerStyle()} border-b border-text-white`
                              : `${navigationMenuTriggerStyle()} border-b border-transparent hover:border-text-white/60`,
                          )}
                          onFocus={() => setOpenDesktopDropdown(navItemKey)}
                        >
                          <Link href={href ?? '#'}>{link.label}</Link>
                        </NavigationMenuLink>
                        {openDesktopDropdown === navItemKey ? (
                          <div className="absolute left-1/2 top-full z-50 -translate-x-1/2 pt-2">
                            <div className="w-[220px] bg-[var(--dark)]/95 shadow-xl shadow-black/40">
                              <ul className="flex flex-col py-2">
                                {childLinks.map((child) => (
                                  <li key={child.id ?? child.href}>
                                    <Link
                                      href={child.href}
                                      className="block px-4 py-2 text-base text-text-white/80 transition-colors hover:bg-white/5 hover:text-text-white focus-visible:outline-hidden"
                                      onClick={() => setOpenDesktopDropdown(null)}
                                    >
                                      {child.label}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        ) : null}
                      </NavigationMenuItem>
                    )
                  }

                  // Regular nav item
                  if (!href) return null

                  return (
                    <NavigationMenuItem key={i}>
                      <NavigationMenuLink
                        asChild
                        className={cn(
                          pathname === href || (href !== '/' && pathname.startsWith(href))
                            ? `${navigationMenuTriggerStyle()} border-b border-text-white`
                            : `${navigationMenuTriggerStyle()} border-b border-transparent hover:border-text-white/60`,
                        )}
                      >
                        <Link href={href}>{link.label}</Link>
                      </NavigationMenuLink>
                    </NavigationMenuItem>
                  )
                })}
              </NavigationMenuList>
              <NavigationMenuIndicator />
              <NavigationMenuViewport />
            </NavigationMenu>
          </div>

          {/* Desktop Theme Toggle + Contact Button */}
          <div className="hidden xl:flex items-center gap-4 flex-shrink-0">
            <ThemeToggle />
            <Link
              href="/kontakt"
              className="inline-flex items-center gap-2 bg-[var(--hotline-red)] px-4 py-2 text-white text-base font-ui hover:bg-[var(--hotline-red)]/90 transition-colors duration-200"
            >
              <div className="w-1 h-1 bg-white"></div>
              <span>24H Hotline</span>
            </Link>
          </div>

          {/* Mobile Theme Toggle + Menu Button */}
          <div className="xl:hidden flex items-center gap-4">
            <ThemeToggle />
            <button
              onClick={toggleMobileMenu}
              className="flex items-center justify-center w-6 h-6 text-white"
              aria-label="Toggle mobile menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                {isMobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="xl:hidden fixed inset-0 z-60 bg-black/90"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            {/* Close button positioned at top right */}
            <motion.button
              onClick={toggleMobileMenu}
              className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 text-white hover:text-white/80 transition-colors duration-200 z-10"
              aria-label="Close mobile menu"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2, delay: 0.1 }}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </motion.button>

            <motion.div
              className="flex flex-col items-center justify-center h-full space-y-8"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              transition={{
                duration: 0.5,
                ease: 'easeOut',
                delay: 0.1,
              }}
            >
              {/* Mobile Navigation */}
              <nav className="flex flex-col items-center space-y-6" aria-label="Hauptnavigation">
                {navItems.map((navItem, index) => {
                  const { link } = navItem
                  const href = getLinkHref(link)

                  if (!href) return null

                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 20 }}
                      transition={{
                        duration: 0.4,
                        ease: 'easeOut',
                        delay: 0.2 + index * 0.1,
                      }}
                    >
                      <Link
                        href={href}
                        onClick={() => {
                          setActiveItem(link.label || '')
                          setIsMobileMenuOpen(false)
                        }}
                        className={cn(
                          'text-white text-2xl font-normal transition-colors duration-200 hover:text-white/80',
                          activeItem === link.label ? 'text-white' : 'text-white/70',
                        )}
                      >
                        {link.label}
                      </Link>
                    </motion.div>
                  )
                })}
              </nav>

              {/* Mobile Contact Button */}
              <motion.div
                className="mt-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{
                  duration: 0.4,
                  ease: 'easeOut',
                  delay: 0.6,
                }}
              >
                <Link
                  href="/kontakt"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="inline-flex items-center gap-2 bg-[var(--hotline-red)] px-6 py-3 text-white text-lg font-normal hover:bg-[var(--hotline-red)]/90 transition-colors duration-200"
                >
                  <div className="w-1 h-1 bg-white"></div>
                  <span>24H-Hotline</span>
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  )
}

export default function HeaderClient({ data, latestProjects, logo }: HeaderClientProps) {
  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        console.error('Header component error:', error, errorInfo)
        // Here you could send error to logging service
      }}
    >
      <HeaderContent data={data} latestProjects={latestProjects} logo={logo} />
    </ErrorBoundary>
  )
}
